import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { BarChart2, FileText, Headphones, Play, HelpCircle, Settings, X, Dumbbell, LogOut, User, Crown, Wrench, BookOpen, Mail, LifeBuoy, Users, Compass } from 'lucide-react'
import { LinkedIn } from '@mui/icons-material'
import { PaymentSubscriptionOptionToPaymentLink, PaymentSubscriptionOption } from "interfaces/subscriptions"
import { clearCache, SERVICE_CALLS } from 'extension-helpers'
import { TeamProvisioningDropdown } from '../TeamProvisioning'
import { useUserData } from '../../lib/redux/users/user-data-hook'
import { Typography } from '../../interfaces/typography'
import { useTeamAccountType } from '../../lib/redux/users/team-account-type-hook'
import { apiKeyStorageKey, removeLocalStorage } from 'cfg/localstorage'

export const SideBar: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isProfileOpen, setIsProfileOpen] = useState(false)
  const [emailCopied, setEmailCopied] = useState(false)
  const [divetStyle, setDivetStyle] = useState({})
  const navRef = useRef<HTMLElement | null>(null)
  const profileRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const navigate = useNavigate()
  
  const { userRaw: user, userAuth, team, canProvision, canConfigure } = useUserData()
  const { isLegal } = useTeamAccountType()
  const isPaidUser = team?.subscription_type === 'TEAM' || user?.autodialer_paid

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const openModal = () => setIsModalOpen(true)
  const closeModal = () => {
    setIsModalOpen(false)
    setEmailCopied(false)
  }
  const toggleProfile = () => setIsProfileOpen(!isProfileOpen)

  const handleLogout = () => {
    removeLocalStorage(apiKeyStorageKey)
    navigate('/login')
  }

  const handleUpgrade = () => {
    window.open(PaymentSubscriptionOptionToPaymentLink[PaymentSubscriptionOption.PARALLEL], '_blank')
    clearCache(SERVICE_CALLS.USER_INFO)
    clearCache(SERVICE_CALLS.AUTODIALER_INFO)
  }

  const handleCopyEmail = () => {
    navigator.clipboard.writeText('support@claracall.ai').then(() => {
      setEmailCopied(true)
      setTimeout(() => setEmailCopied(false), 2000)
    })
  }

  const navItems = [
    { name: 'Analytics', icon: BarChart2, slugs: ['analytics', 'reports', 'dashboard', 'research', 'diagnostics', 'prime-time', 'number-health', 'geography', 'one-on-ones', ''] },
    { name: 'Transcripts', icon: FileText, slugs: ['transcripts'] },
    { name: 'Live', icon: Headphones, slugs: ['live'] },
    ...(canConfigure ? [{ name: 'Configuration', icon: Wrench, slugs: ['configuration'] }] : []),
  ]
  const settingsSlugs = ['settings', 'autodialer-settings', 'subteams', 'client-accounts', 'keywords', 'user-management', 'email-settings', 'voicemail-recordings', 'number-registration', 'pages/upload']

  const getBasePath = (path: string) => {
    return '/' + path.split('/')[1].split('?')[0]
  }

  const isActive = (slugs: string[]) => {
    const currentBasePath = getBasePath(location.pathname)
    return slugs.some(slug => {
      const slugPath = slug === '' ? '/' : `/${slug}`
      return currentBasePath === slugPath
    })
  }

  const isSettingsActive = isActive(settingsSlugs)

  const pointDivetTo = useCallback((activeElement: Element | undefined | null, transition: boolean = true) => {
    if (!navRef.current || !activeElement) { return }
    const { left, width } = activeElement.getBoundingClientRect()
    const navLeft = navRef.current.getBoundingClientRect().left
    setDivetStyle({
      left: `${left - navLeft + width / 2}px`,
      transition: transition ? 'left 0.3s ease-in-out' : '',
    })
  }, [])

  const updateDivetPosition = useCallback((transition: boolean = true) => {
    const activeElement = navRef.current?.querySelector(`[data-active="true"]`)
    pointDivetTo(activeElement, transition)
  }, [pointDivetTo])

  const setNavRef = useCallback((navNode: HTMLElement | undefined | null) => {
    navRef.current = navNode ?? null
    updateDivetPosition(false)
  }, [updateDivetPosition, canConfigure])  // rendered locations depend on canConfigure

  useEffect(() => {
    const handler = () => updateDivetPosition(false)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [updateDivetPosition])

  return (
    <div className='pt-1.5 pb-1'>
      <nav
        ref={setNavRef}
        className="bg-gradient-to-r w-full from-slate-900 via-slate-800 to-slate-900 text-white px-6 py-3 flex items-center justify-between relative"
        style={{borderRadius: '8px'}}
      >
        {/* Selection Indicator */}
        <div 
          className="absolute w-0 h-0 border-l-[6px] border-r-[6px] border-b-[6px] border-l-transparent border-r-transparent border-b-white"
          style={{
            ...divetStyle,
            bottom: '0',
            transform: 'translateX(-50%)'
          }}
        />
        
        {/* Logo and Navigation Container */}
        <div className="flex items-center">
          {/* Logo */}
          <div className="mr-5 transition-transform hover:scale-105 select-none">
            <svg
              width="65"
              height="32"
              viewBox="0 0 65 96"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={"text-cyan-300"}
              aria-hidden="true"
            >
              <path d="M26.4965 18.0458C27.3425 18.6492 28.0345 19.9861 28.0345 21.0273V46.0389C28.0345 47.0741 27.3425 47.435 26.4965 46.8375L1.544 29.2265C0.697999 28.629 0.00589752 27.292 0.00589752 26.2509V1.08547C0.00589752 0.0502189 0.697999 -0.310639 1.544 0.292763L26.5025 18.0458H26.4965ZM46.8045 35.6746C45.9595 35.0712 44.5805 35.0712 43.7345 35.6746L1.5321 65.8624C0.686204 66.4658 0 67.8087 0 68.8439V94.3051C0 95.3403 0.692105 95.6953 1.5321 95.0919L64.1265 50.2154C64.9665 49.612 64.9665 48.624 64.1265 48.0206L46.8105 35.6687L46.8045 35.6746Z" fill="url(#paint0_linear_3836_7223)"/>
              <defs>
                <linearGradient id="paint0_linear_3836_7223" x1="0" y1="47.6953" x2="64.7595" y2="47.6953" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#0C93E0"/>
                  <stop offset="0.45" stopColor="#18BDD6"/>
                  <stop offset="0.79" stopColor="#20D8D0"/>
                  <stop offset="1" stopColor="#23E2CE"/>
                </linearGradient>
              </defs>
            </svg>
            <span className="sr-only">Home</span>
          </div>

          {/* Navigation Items */}
          <div className="flex items-center space-x-2">
            {navItems.map((item) => {
              const active = isActive(item.slugs)
              return (
                <Link
                  key={item.name}
                  to={`/${item.slugs[0]}`}
                  data-active={active}
                  ref={active ? pointDivetTo : undefined}
                  className={`relative px-3 py-2 rounded-md transition-all duration-200 ${
                    active  ? 'text-white bg-slate-700/40'
                    : 'text-slate-200 hover:text-white hover:bg-slate-700/30'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <item.icon className={`w-5 h-5 ${
                      active  ? 'text-cyan-300' : ''
                    }`} aria-hidden="true" />
                    <span className={`hidden font-medium ${(canConfigure ? 'min-[1350px]:inline' : 'min-[900px]:inline')
                    }`}>
                      {item.name}
                    </span>
                    <span className={`sr-only ${(canConfigure ? 'min-[1350px]:hidden' : 'min-[900px]:hidden')
                    }`}>
                      {item.name}
                    </span>
                  </div>
                </Link>
              )
            })}
          </div>
        </div>

        {/* Right Icons */}
        <div className="flex items-center gap-2">
        {/* Team Provisioning Dropdown */}
        {canProvision && <TeamProvisioningDropdown />}

          {!isLegal && (
            <button
              className={`p-2 rounded-md transition-all duration-200 hover:bg-slate-700/30'`}
              onClick={openModal}
              aria-label="Get help"
            >
              <HelpCircle className="w-5 h-5" aria-hidden="true" />
            </button>
          )}
          <Link
            to="/settings"
            data-active={isSettingsActive}
            ref={isSettingsActive ? pointDivetTo : undefined}
            className={`p-2 rounded-md transition-all duration-200 ${
              isActive(['settings'])
                ?  'bg-slate-700/40'
                :  'hover:bg-slate-700/30'
            }`}
            aria-label="Settings"
          >
            <Settings className="w-5 h-5" aria-hidden="true" />
          </Link>
          
         
          <div className="relative" ref={profileRef}>
            <button
              className={`p-2 rounded-md transition-all duration-200 ${
                isProfileOpen
                  ? 'bg-slate-700/40'
                  : 'hover:bg-slate-700/30'
              }`}
              onClick={toggleProfile}
              aria-label="Profile"
            >
              <User className="w-5 h-5" aria-hidden="true" />
            </button>
            {isProfileOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg py-2 z-50">
                <div className="px-4 py-2 border-b border-gray-200">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user?.user_name}
                    {userAuth?.email && (
                      <span className="text-xs text-gray-600 font-normal"> ({userAuth.email})</span>
                    )}
                  </p>
                  <div className="mt-1 flex items-center gap-1">
                    {isPaidUser ? (
                      <>
                        <Crown className="w-4 h-4 text-yellow-500" />
                        <span className="text-sm text-gray-600">Paid Account</span>
                      </>
                    ) : (
                      <button 
                        onClick={handleUpgrade}
                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        <Crown className="w-4 h-4" />
                        <span>Upgrade Account</span>
                      </button>
                    )}
                  </div>
                </div>
                {!isLegal && (
                  <Link
                    to="/support"
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 border-b border-gray-200"
                    onClick={() => setIsProfileOpen(false)}
                  >
                    <LifeBuoy className="w-4 h-4" />
                    <span>Support</span>
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Log out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Help Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full" role="dialog" aria-modal="true" aria-labelledby="modal-title">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <Typography variant="h3" color="#1f2937">Need Help?</Typography>
              <button 
                onClick={closeModal} 
                className="text-gray-400 hover:text-gray-500 hover:bg-gray-100 p-1 rounded-lg transition-colors"
                aria-label="Close modal"
              >
                <X className="w-6 h-6" aria-hidden="true" />
              </button>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <Typography variant="h4" color="#1f2937" style={{ marginBottom: '0.5rem' }}>How can we help?</Typography>
                  <Typography variant="smallParagraph" color="#6b7280" style={{ marginBottom: '1rem' }}>
                    Choose how you'd like to get assistance:
                  </Typography>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() => {
                      navigate('/onboarding')
                      closeModal()
                    }}
                    className="flex items-center gap-3 w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white p-3 rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    <Compass className="w-5 h-5" />
                    <div className="text-left">
                      <Typography variant="mediumParagraph" fontWeight="500" color="white">Onboarding Guide</Typography>
                      <Typography variant="smallParagraph" color="white" style={{ opacity: 0.9 }}>Step-by-step setup guide</Typography>
                    </div>
                  </button>

                  <button
                    onClick={handleCopyEmail}
                    className="flex items-center gap-3 w-full bg-gradient-to-r from-slate-600 to-slate-700 text-white p-3 rounded-lg hover:from-slate-700 hover:to-slate-800 transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    <Mail className="w-5 h-5" />
                    <div className="text-left">
                      <Typography variant="mediumParagraph" fontWeight="500" color="white">Email Support</Typography>
                      <Typography variant="smallParagraph" color="white" style={{ opacity: 0.9 }}>
                        {emailCopied ? 'Email copied!' : 'support@claracall.ai'}
                      </Typography>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      navigate('/support')
                      closeModal()
                    }}
                    className="flex items-center gap-3 w-full bg-gradient-to-r from-cyan-600 to-cyan-700 text-white p-3 rounded-lg hover:from-cyan-700 hover:to-cyan-800 transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    <BookOpen className="w-5 h-5" />
                    <div className="text-left">
                      <Typography variant="mediumParagraph" fontWeight="500" color="white">Support Center</Typography>
                      <Typography variant="smallParagraph" color="white" style={{ opacity: 0.9 }}>Learn how to use ClaraCall</Typography>
                    </div>
                  </button>
                </div>

                {!isPaidUser && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                    <Typography variant="smallParagraph" color="#6b7280">
                      💡 Upgrade to get priority support via your team's Slack channel
                    </Typography>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
